import * as vp from 'vopay';
import { Box, Title, Table, Card } from 'vopay';
import { Button } from 'UI';
import { FunctionComponent, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import useFetchUsers from '@/hooks/useApi/useFetchUsers';
import userPostDeleteUser from '@/hooks/useApi/usePostDeleteUser';
import DeleteUserContext, { DeleteUserCtx } from './DeleteUser/DeleteUserContext';
import DeleteUserDialogue from './DeleteUser/DeleteUserDialogue';
import { DeleteUserDialogueHandler, DialogueHandlerProps } from './types';
import { useTranslation } from 'react-i18next';
import { usePartnerData } from '@/providers/PartnerData.provider';

type FC = FunctionComponent;

const UsersList: FC = () => {
  const [shouldFetchUsers, setFetchUsers] = useState(true);
  const [DeleteUserError, setDeleteUserError] = useState('');
  const [showDeleteUserErrorAlert, setShowDeleteUserErrorAlert] = useState(false);
  const [showDeleteUserSuccessAlert, setShowDeleteUserSuccessAlert] = useState(false);
  const [showDeleteUserConfirmationAlert, setshowDeleteUserConfirmationAlert] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [shouldDeleteUser, setShouldDeleteUser] = useState(false);
  const [DeleteUserResponse, deleteUser] = userPostDeleteUser({});
  const [users, fetchUsers] = useFetchUsers({});
  const history = useHistory();
  const { t } = useTranslation('translations', { keyPrefix: 'Users.List' });
  const {
    response: { isRoleViewOnly },
  } = usePartnerData();

  useEffect(() => {
    if (!shouldFetchUsers) return;
    fetchUsers();
  }, [shouldFetchUsers]);

  const tableProps: vp.TableProps<{
    Username: string;
    UserType: string;
    DateAdded: string;
  }> = {
    id: 'users-list',
    version: '2',
    zebra: true,
    columns: [
      {
        id: 'Username',
        header: t('Table.username'),
        align: 'left',
        field: 'Username',
      },
      {
        id: 'UserType',
        header: t('Table.userType'),
        align: 'left',
        field: 'UserType',
      },
      {
        id: 'DateAdded',
        header: t('Table.dateAdded'),
        align: 'left',
        field: 'DateAdded',
      },
      {
        id: 'view',
        align: 'right',
        header: 'Actions',
        template: ({ data }) => (
          <Box jc="fe" gap="sm">
            <Button
              id={`user-change-${data.Username}`}
              size="small"
              disabled={isRoleViewOnly}
              onClick={() => {
                history.push(`/users/change-password/${data.Username}`);
              }}
            >
              {t('Table.changePasswordButton')}
            </Button>
            <Button
              id={`user-role-change-${data.Username}`}
              size="small"
              disabled={isRoleViewOnly}
              onClick={() => {
                history.push(`/users/change-role/${data.Username}/${data.UserType}`);
              }}
            >
              {t('Table.changeRoleButton')}
            </Button>
            <Button
              id={`user-delete-${data.Username}`}
              size="small"
              disabled={isRoleViewOnly}
              onClick={() => {
                setEmailAddress(data.Username);
                setshowDeleteUserConfirmationAlert(true);
              }}
            >
              {t('Table.removeButton')}
            </Button>
          </Box>
        ),
      },
    ],
    rows: Object.values(users.Users || {}).map((user) => ({
      id: user.Username,
      data: user,
    })),
  };

  const DeleteUser: DeleteUserCtx = {
    showDeleteUserErrorAlert,
    showDeleteUserSuccessAlert,
    showDeleteUserConfirmationAlert,
    DeleteUserError,
  };

  useEffect(() => {
    if (shouldDeleteUser) {
      setShouldDeleteUser(false);
      const { Success, ErrorMessage } = DeleteUserResponse;
      if (Success === true) {
        setShowDeleteUserSuccessAlert(true);
      } else {
        setDeleteUserError(ErrorMessage);
        setShowDeleteUserErrorAlert(true);
      }
    }
  }, [DeleteUserResponse]);

  const handlers = {
    handleDialogue: (handlerProps: DialogueHandlerProps<DeleteUserDialogueHandler>) => {
      const { type, state, action = 'default' } = handlerProps;

      const dialogueHandlers: DeleteUserDialogueHandler = {
        delete: {
          success: {
            default: () => {
              setShowDeleteUserSuccessAlert(false);
              setFetchUsers(true);
              fetchUsers();
            },
          },
          fail: {
            default: () => {
              setShowDeleteUserErrorAlert(false);
              setDeleteUserError('');
            },
          },
        },
        alertDelete: {
          success: {
            default: () => {
              setshowDeleteUserConfirmationAlert(false);

              const DeleteUserRequest = {
                EmailAddress: emailAddress,
              };
              setShouldDeleteUser(true);
              deleteUser(DeleteUserRequest);
            },
          },
          fail: {
            default: () => {
              setshowDeleteUserConfirmationAlert(false);
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },
  };

  return (
    <div>
      <DeleteUserContext.Provider value={DeleteUser}>
        <Box mb="md" jc="sb" ai="center">
          <Title title={t('pageTitle')} />
          <Button
            id="showHideAccountCredentials"
            colorScheme="primary"
            disabled={isRoleViewOnly}
            onClick={() => {
              history.push('/users/create');
            }}
          >
            {t('addUserButton')}
          </Button>
        </Box>
        <Card
          style={{
            padding: '16px',
          }}
        >
          <Table {...tableProps} />
        </Card>
        <DeleteUserDialogue handleDialogue={handlers.handleDialogue} />
      </DeleteUserContext.Provider>
    </div>
  );
};
export default UsersList;
